import {
  BarChart3,
  Bell,
  ChevronLeft,
  ChevronRight,
  Headphones,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  ShieldAlert,
  Settings,
  Ticket,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { Avatar } from "../components/Avatar";
import { BrandLogo } from "../components/BrandLogo";
import { ThemeToggle } from "../components/ThemeToggle";
import { NotificationBell } from "../components/NotificationBell";
import { useSession } from "../context/SessionContext";

const LINKS = [
  { to: "/agent", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/agent/tickets", label: "Tickets", icon: Ticket },
  { to: "/agent/spam", label: "Spam & quarantine", icon: ShieldAlert, permission: "ticket.view" },
  { to: "/agent/customers", label: "Customers", icon: Users },
  { to: "/agent/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/agent/notifications", label: "Notifications", icon: Bell },
  { to: "/agent/settings", label: "Settings", icon: Settings },
];

export function AgentShell() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const visibleLinks = LINKS.filter((link) => !("permission" in link) || (user?.kind === "staff" && (user.permissions.includes(link.permission!) || user.permissions.includes("*"))));

  async function signOut() {
    await logout();
    navigate("/agent/login", { replace: true });
  }

  return (
    <div className={`agent-app${collapsed ? " agent-app--collapsed" : ""}`}>
      <a className="skip-link" href="#agent-main">Skip to workspace</a>
      {mobileOpen && <button className="sidebar-scrim" type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
      <aside className={`agent-sidebar${mobileOpen ? " is-open" : ""}`}>
        <div className="agent-sidebar__brand">
          <BrandLogo compact={collapsed} showProduct={false} />
          <button className="icon-button icon-button--compact desktop-only" type="button" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}>
            {collapsed ? <ChevronRight aria-hidden="true" size={17} /> : <ChevronLeft aria-hidden="true" size={17} />}
          </button>
          <button className="icon-button mobile-only" type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X aria-hidden="true" /></button>
        </div>
        <nav className="agent-nav" aria-label="Agent navigation">
          {visibleLinks.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} title={collapsed ? label : undefined} onClick={() => setMobileOpen(false)}>
              <Icon aria-hidden="true" size={19} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <a className="agent-sidebar__help" href="https://docs.next-cart.com/" target="_blank" rel="noreferrer">
          <Headphones aria-hidden="true" size={19} />
          <div><strong>Need a hand?</strong><span>Open Next-Cart Docs</span></div>
        </a>
        <div className="agent-sidebar__profile">
          <Avatar name={user?.name ?? "Agent"} url={user?.avatarUrl} status="online" />
          <div><strong>{user?.name ?? "Agent"}</strong><span>{user?.kind === "staff" ? user.roles[0] ?? "Agent" : "Agent"}</span></div>
          <button className="icon-button icon-button--compact" type="button" onClick={signOut} aria-label="Sign out"><LogOut aria-hidden="true" size={17} /></button>
        </div>
      </aside>

      <div className="agent-stage">
        <header className="agent-topbar">
          <button className="icon-button mobile-only" type="button" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu aria-hidden="true" /></button>
          <button className="agent-search" type="button" onClick={() => navigate("/agent/tickets?focus=search")}>
            <Search aria-hidden="true" size={18} /><span>Search tickets and customers</span><kbd>/</kbd>
          </button>
          <div className="agent-topbar__actions">
            <NotificationBell />
            <ThemeToggle compact />
          </div>
        </header>
        <main id="agent-main" className="agent-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <nav className="agent-bottom-nav" aria-label="Mobile agent navigation">
        {visibleLinks.filter((link) => link.to !== "/agent/spam").slice(0, 4).map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}><Icon aria-hidden="true" /><span>{label}</span></NavLink>
        ))}
      </nav>
    </div>
  );
}
